/**
 * GovernanceLoopDiagram (F069)
 *
 * Circular loop visualization showing the compounding governance cycle.
 * Loop: Policies → Enforcement → Evidence → Insights → Recommendations → Policy Improvement
 *
 * Placement: Executive Command, Reports
 */
import React from 'react';
import { makeStyles, tokens, Body2 } from '@fluentui/react-components';
import { governanceTokens } from './design-tokens';
import { solutionTokens } from '../../theme/solution-theme';

export interface GovernanceLoopMetrics {
  evaluations30d: number;
  evidenceGenerated: number;
  recommendationsProduced: number;
  policiesUpdated: number;
  controlsImproved: number;
  frameworkCoverageDelta: number;
}

export interface GovernanceLoopDiagramProps {
  metrics: GovernanceLoopMetrics;
  period?: '7d' | '30d' | '90d';
}

interface LoopSegment {
  id: string;
  label: string;
  value: string;
  color: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
  },
  loop: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalM,
    maxWidth: '600px',
  },
  segment: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalS,
    minWidth: '100px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    position: 'relative',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  segLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
  },
  segValue: {
    fontWeight: tokens.fontWeightSemibold as any,
    textAlign: 'center',
  },
  arrow: {
    display: 'flex',
    alignItems: 'center',
    color: governanceTokens.colorPipelineConnector,
    // 18px: no exact Fluent fontSize token (16/20 are the neighbours) — left as-is.
    fontSize: '18px',
    alignSelf: 'center',
  },
  centerLabel: {
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

export const GovernanceLoopDiagram: React.FC<GovernanceLoopDiagramProps> = ({
  metrics,
  period = '30d',
}) => {
  const styles = useStyles();

  const segments: LoopSegment[] = [
    { id: 'enforce', label: 'Enforcement', value: `${metrics.evaluations30d} eval`, color: governanceTokens.colorGovernanceSecondary },
    { id: 'evidence', label: 'Evidence', value: `${metrics.evidenceGenerated} artifacts`, color: governanceTokens.colorGovernancePrimary },
    { id: 'insights', label: 'Insights', value: `${metrics.recommendationsProduced} recs`, color: solutionTokens.brandSecondary },
    { id: 'improve', label: 'Improved', value: `${metrics.policiesUpdated} policies`, color: governanceTokens.colorStatusSuccess },
    { id: 'controls', label: 'Controls', value: `${metrics.controlsImproved} improved`, color: governanceTokens.colorGovernanceSecondary },
    { id: 'coverage', label: 'Coverage', value: `${metrics.frameworkCoverageDelta > 0 ? '+' : ''}${metrics.frameworkCoverageDelta.toFixed(1)}%`, color: governanceTokens.colorStatusSuccess },
  ];

  return (
    <div className={styles.container}>
      <Body2 className={styles.centerLabel}>Governance Loop — Last {period}</Body2>
      <div className={styles.loop}>
        {segments.map((seg, idx) => (
          <React.Fragment key={seg.id}>
            {idx > 0 && <span className={styles.arrow}>→</span>}
            <div className={styles.segment}>
              <div className={styles.dot} style={{ backgroundColor: seg.color }} />
              <span className={styles.segLabel}>{seg.label}</span>
              <Body2 className={styles.segValue} style={{ color: seg.color }}>
                {seg.value}
              </Body2>
            </div>
          </React.Fragment>
        ))}
        <span className={styles.arrow}>↩</span>
      </div>
    </div>
  );
};
